import { z } from "zod";

import {
	envSchema as sharedApiEnvSchema,
	envExamples as sharedApiEnvExamples,
} from "@intents-explorer/shared-api/env-schema";

// Server-only variables (extend shared-api schema)
export const serverEnvSchema = sharedApiEnvSchema.extend({
	DATA_PLATFORM_API_URL: z.url().describe("Base URL for the Data Platform API"),
	DATA_PLATFORM_API_KEY: z
		.string()
		.min(1)
		.describe("Data Platform API key with access to explorer volume"),
	ONE_CLICK_API_BASE_URL: z.string().min(1).describe("Base URL for 1Click API"),
	SHIELD_API_URL: z
		.url()
		// A trailing slash would make every path double up: `//public/status` 404s.
		.transform((url) => {
			return url.replace(/\/+$/, "");
		})
		.describe(
			"Base URL for the Shield API: https://shield.chaindefuser.com (production), https://shield-stage.defuse.org (staging)",
		),
	ONE_CLICK_API_TOKEN: z
		.string()
		.describe("Auth token for 1Click API (can be empty)"),
	VERCEL_PROJECT_PRODUCTION_URL: z
		.string()
		.optional()
		.describe("Vercel production URL (auto-set by Vercel)"),
	KV_REST_API_URL: z.string().optional().describe("Vercel KV REST API URL"),
	KV_REST_API_TOKEN: z.string().optional().describe("Vercel KV REST API token"),
	ENABLE_UPSTASH: z
		.string()
		.optional()
		.describe(
			"Set to 'true' to enable Upstash Redis (disabled by default; in-memory cache is used when off)",
		),
	SENTRY_AUTH_TOKEN: z
		.string()
		.optional()
		.describe("Sentry auth token for source maps"),
	HEALTH_CHECK_TOKEN: z
		.string()
		.optional()
		.describe(
			"Shared secret required by /health; the endpoint 404s when unset or mismatched",
		),
	PARTNERS_API_TOKEN: z
		.string()
		.min(1)
		.describe(
			"Bearer token for partners.near-intents.org API (used to fetch per-partner rate limits)",
		),
	ENABLE_V1_API: z
		.string()
		.optional()
		.describe(
			"Set to 'true' to enable the /api/v1 endpoints (disabled by default)",
		),
});

// Client-safe variables (NEXT_PUBLIC_* prefix)
// These are embedded in the client bundle by Next.js
const clientEnvSchema = z.object({
	NEXT_PUBLIC_THROTTLE_1CLICK_DB_REQUESTS: z
		.string()
		.optional()
		.describe("Set to 'true' to throttle DB requests"),
	NEXT_PUBLIC_SENTRY_ENABLED: z
		.string()
		.optional()
		.describe("Set to 'true' to enable Sentry"),
	NEXT_PUBLIC_HIDE_CONFIDENTIAL: z
		.string()
		.optional()
		.describe("Set to 'true' to disable masking/confidential functionality"),
});

export const envSchema = serverEnvSchema.extend(clientEnvSchema.shape);

/** @public Used by the repository-level environment metadata generator. */
export const envExamples: Record<keyof z.infer<typeof envSchema>, string> = {
	...sharedApiEnvExamples,
	DATA_PLATFORM_API_URL: "https://platform.data.defuse.org",
	DATA_PLATFORM_API_KEY: "your-data-platform-api-key",
	ONE_CLICK_API_BASE_URL: "https://1click.chaindefuser.com",
	SHIELD_API_URL: "https://shield.chaindefuser.com",
	ONE_CLICK_API_TOKEN: "",
	VERCEL_PROJECT_PRODUCTION_URL: "http://localhost:3001",
	KV_REST_API_URL: "https://kv.vercel.com",
	KV_REST_API_TOKEN: "TOKEN",
	ENABLE_UPSTASH: "false",
	SENTRY_AUTH_TOKEN: "",
	HEALTH_CHECK_TOKEN: "",
	PARTNERS_API_TOKEN: "your-partners-api-token",
	ENABLE_V1_API: "true",
	NEXT_PUBLIC_THROTTLE_1CLICK_DB_REQUESTS: "false",
	NEXT_PUBLIC_SENTRY_ENABLED: "false",
	NEXT_PUBLIC_HIDE_CONFIDENTIAL: "false",
};

export type Env = z.infer<typeof envSchema>;
